import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeoSetTargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { Button, EmptyState, Icon, Table } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import geoFiltersColumns from './geoFiltersColumns';

interface IGeoFiltersTableProps {
  defaultGeoSetId?: string;
  onCreateSet: () => void;
  onEditSet: (targetable: GeoSetTargetable) => void;
  onSetDefault: (targetable: GeoSetTargetable) => void;
}

const DEFAULT_PAGE_SIZE = 20;

function GeoFiltersTable({
  defaultGeoSetId,
  onCreateSet,
  onEditSet,
  onSetDefault,
}: Readonly<IGeoFiltersTableProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetables, Targetings } = useStore();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const geoSetTargetables = Targetables.list({
    filters: { is_archived__ne: true, _cls: TargetableClass.geo_set },
  });

  const geoSetTargetings = Targetings.list({
    filters: { kind: 'geo_set', is_archived__ne: true },
  });

  const campaignCountsByGeoSetId = new Map<string, number>();
  for (const targeting of geoSetTargetings.models) {
    const targetData = targeting.get('target_data');
    if (targetData) {
      campaignCountsByGeoSetId.set(targetData, (campaignCountsByGeoSetId.get(targetData) || 0) + 1);
    }
  }

  if (geoSetTargetables.isLoaded && geoSetTargetables.models.length === 0) {
    return (
      <EmptyState
        description={t('Get started by creating a new set.')}
        height={'full'}
        label={t('No geofilters')}
        theme={'white'}
        width={'full'}
      >
        <Button onClick={onCreateSet} prefix={<Icon icon={faPlus} />}>
          {t('Create set')}
        </Button>
      </EmptyState>
    );
  }

  const models = geoSetTargetables.models as unknown as GeoSetTargetable[];
  const sortedModels = [...models].sort((a, b) => {
    const aIsDefault = a.isDefault(defaultGeoSetId);
    const bIsDefault = b.isDefault(defaultGeoSetId);
    if (aIsDefault && !bIsDefault) {
      return -1;
    }
    if (!aIsDefault && bIsDefault) {
      return 1;
    }
    return (a.get('name') || '').localeCompare(b.get('name') || '');
  });

  const startIndex = page * pageSize;
  const paginatedModels = sortedModels.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.ceil(sortedModels.length / pageSize);

  function handlePageChange(newPage: number): void {
    setPage(newPage);
  }

  function handlePageSizeChange(newPageSize: number, newPage: number): void {
    setPageSize(newPageSize);
    setPage(newPage);
  }

  return (
    <Table<GeoSetTargetable>
      columns={geoFiltersColumns(
        t,
        onEditSet,
        onSetDefault,
        defaultGeoSetId,
        campaignCountsByGeoSetId,
      )}
      idKey={'id'}
      isLoading={geoSetTargetables.isPending}
      items={paginatedModels}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      page={page}
      pages={totalPages}
      pageSize={pageSize}
    />
  );
}

export default observer(GeoFiltersTable);
